import { AutocompleteInputChangeReason } from "@mui/material/useAutocomplete";
import React from "react";
import { CampaignInfoView } from "@tiny/api";
import { usePutCampaignFindFilterBasicQuery } from "@tiny/api";
import { AutocompleteField } from "@tiny/components";
import useDebounce from "@tiny/components/src/hooks/useDebounce";

export interface CampaignsInputProps {
  value?: string[];
  onChangeValue?: (value: string[]) => void;
  helperText?: string;
  error?: boolean;
  inputRef?: React.ForwardedRef<HTMLInputElement>;
  disabled?: boolean;
  multiple?: boolean;
}

export function CampaignsInput(props: CampaignsInputProps) {
  const {
    value,
    onChangeValue,
    helperText,
    error,
    inputRef,
    disabled,
    multiple = true,
  } = props;

  const [internalCampaignValue, setInternalCampaignValue] =
    React.useState<CampaignInfoView[]>();

  const [searching, setSearching] = React.useState(false);

  const [search, setSearch] = React.useState("");

  const searchDebounced = useDebounce(search, 500);

  const { data: { content: campaignValue = [] } = {} } =
    usePutCampaignFindFilterBasicQuery(
      {
        ids: value,
      },
      {},
      {
        enabled: !!value && value.length > 0 && !internalCampaignValue,
      }
    );

  const { data: { content: searchResult = [] } = {}, isLoading } =
    usePutCampaignFindFilterBasicQuery(
      {
        searchTextName: searchDebounced,
        campaignStatuses: ["COMPLETED"],
      },
      {
        page: 0,
        size: 10,
      },
      {
        enabled: searching,
      }
    );

  const changeInput = (
    _event: React.ChangeEvent<{}>,
    v: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (searching) {
      if (reason === "input") {
        setSearch(v);
      }
    } else {
      setSearch(v);
    }
  };

  const changeValue = (v: CampaignInfoView[] | CampaignInfoView | null) => {
    if (multiple) {
      const selected = (v as CampaignInfoView[]) ?? [];
      setInternalCampaignValue(selected);
      onChangeValue?.(selected.map((item) => item.id!));
    } else {
      // MUI's Autocomplete hands back a single option (or null) when
      // multiple=false, not an array — keep internal state array-shaped
      // regardless, so the value passed to AutocompleteField below stays
      // consistent between single/multi mode.
      const selected = v as CampaignInfoView | null;
      setInternalCampaignValue(selected ? [selected] : []);
      onChangeValue?.(selected?.id ? [selected.id] : []);
    }
  };

  const open = () => {
    setSearching(true);
  };

  const close = () => {
    setSearching(false);
  };

  const selectedCampaigns = internalCampaignValue || campaignValue;
  // MUI's Autocomplete expects a single option (or null) as `value` when
  // multiple=false — passing the array itself (even `[]`) makes it treat
  // that array as "a value is selected" and render getOptionLabel([]) as
  // the input's text (i.e. literally "undefined").
  const autocompleteValue = multiple
    ? selectedCampaigns
    : selectedCampaigns[0] ?? null;

  return (
    <AutocompleteField
      inputRef={inputRef}
      multiple={multiple}
      loading={isLoading}
      options={searchResult}
      inputValue={search}
      value={autocompleteValue}
      onInputChange={changeInput}
      onChangeValue={changeValue}
      onOpen={open}
      onClose={close}
      getOptionLabel={(option) => option?.name}
      limitTags={1}
      helperText={helperText}
      error={error}
      showHelperTextMargin
      disabled={disabled}
      getOptionSelected={(option, value) => {
        return option?.id === value?.id;
      }}
    />
  );
}
